import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { CloseCircleIcon } from '@iconicicons/react';
import { PlotterSettings } from '../plotter/PlotterSettings';
import { PointFilters } from '../filters/PointFilters';
import * as filtersSlice from '../filters/filtersSlice';
import { PinIcon, PinOffIcon } from 'lucide-react';
import { Collapse } from 'bootstrap';

export type MapSettingsProps = {
  onChangeState: (state: boolean) => void;
};

export const MapSettings: React.FC<MapSettingsProps> = ({ onChangeState }) => {
  const [isPinned, setIsPinned] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => onChangeState(isPinned), [isPinned]);
  const collapseRef = useRef<HTMLDivElement>(null);
  const collapse = useMemo(() => {
    if (!collapseRef.current) return null;
    return new Collapse(collapseRef.current, {
      toggle: false,
    });
  }, [collapseRef.current]);

  const hide = useCallback(() => {
    if (isPinned) return;
    collapse?.hide();
  }, [collapse, isPinned]);

  const show = useCallback(() => {
    collapse?.show();
  }, [collapse]);

  const pin = useCallback(() => {
    setIsPinned(true);
    show();
  }, [show]);

  const unpin = useCallback(() => {
    setIsPinned(false);
    hide();
  }, [hide]);

  return (
    <div
      onMouseEnter={show}
      onMouseLeave={hide}
      style={{ overflow: 'hidden' }}
      className="p-3 border-top"
    >
      <div>
        <h5 className="w-100">
          <span>Map Settings</span>
          <span className="float">
            <button
              onClick={() => dispatch(filtersSlice.clearFilters())}
              className="btn btn-primary-outline m-0 p-0"
            >
              <CloseCircleIcon color="red" />
            </button>
          </span>
          <span className="float-end">
            {isPinned ? (
              <button onClick={unpin} className="btn btn-primary-outline">
                <PinIcon size={16} />
              </button>
            ) : (
              <button onClick={pin} className="btn btn-primary-outline">
                <PinOffIcon size={16} />
              </button>
            )}
          </span>
        </h5>
      </div>
      <div
        id="map-settings-collapse"
        className={`mb-4 collapse ${isPinned ? 'show' : ''}`}
        style={{ overflow: 'visible' }}
        ref={collapseRef}
      >
        <PlotterSettings />
        <hr />
        <PointFilters />
      </div>
    </div>
  );
};
